/**
 * Tournament Bracket Component
 *
 * 싱글/더블 엘리미네이션 토너먼트 대진표를 시각화하는 컴포넌트
 * tournament-structure.md 파일의 구조를 기반으로 구현
 *
 * Features:
 * - 싱글 엘리미네이션 (Single Elimination) 대진표
 * - 더블 엘리미네이션 (Double Elimination) 대진표 (승자조, 패자조, 결승)
 * - 선수 클릭 시 승자 선택 기능
 * - 스크롤 가능한 레이아웃
 * - 반응형 디자인
 * 
 * - 팝업 호출시 params - mtmkgCd, armType, mtmkgTycd, mtmkgStat, comptnNm, mtmkgNm
 */

import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { ApiSearch, ApiSave } from '@assets/js/api/commonApi.js';
import '@assets/css/comp-form-trmntBracket.css';

const CompFormtrmntBracket = forwardRef((props, ref) => {
    const [params, setParams] = useState({});
    const [tournamentData, setTournamentData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [eliminationType, setEliminationType] = useState('');

    /****************************
     * Define required variables
    *****************************/
    const rootId = 'modalSheetBracket';

    /*************************
     * Define react functions
    **************************/
    // Forward ref to allow parent components to call event functions.
    useImperativeHandle(ref, () => ({
        modalToggle(p_params) {
            if (isVisible) {
                handleToggle();
            } else {
                setParams(p_params || {});
                setIsVisible(true);
                if (p_params && p_params.mtmkgCd && p_params.armType) {
                    loadTournamentData(p_params.mtmkgCd, p_params.armType);
                }
            }
        },
    }));

    /**********************
     * Define functions
    ***********************/
    const handleToggle = () => {
        setIsVisible(!isVisible);
        if (isVisible) {
            // Reset data when closing
            setTournamentData([]);
            setParams({});
        }
    };

    //토너먼트 초기화.
    const handleInitialize = () => {
        const grouped = groupBySeedType();
        const winnerRounds = groupWinnerByRound(grouped.winner);

        const opt = {
            msg : {
                confirm: '토너먼트를 대진 시작단계로 초기화 하시겠습니까?\n(대진상태: 진행, 순위결과: 삭제)',
                message: '토너먼트가 초기화 되었습니다.',
            },
        }
        const saveParams = {
            mtmkgCd: params.mtmkgCd,
            armType: params.armType,
            round1SeedCnt: winnerRounds[0].length,
            winnerSeedCnt: grouped.winner.length,
        }

        ApiSave('/sys/matchtrmntmng/trmntBracket/trmntInit', saveParams, opt).then(res => {
            if (res && res.success) {
                if (props.onGridSearch) {
                    props.onGridSearch();
                }
                params.mtmkgStat = 'D';

                setParams(params);

                // 데이터 새로고침
                loadTournamentData(params.mtmkgCd, params.armType);
            }
        });
    };

    //토너먼트 상세 Bracket 정보 조회. )
    const loadTournamentData = async (mtmkgCd, armType) => {
        if (!mtmkgCd || !armType) return;

        try {
            setLoading(true);

            ApiSearch('/sys/matchtrmntmng/trmntBracket/search/list', {
                mtmkgCd: mtmkgCd,
                armType: armType
            }).then(res => {
                if (res && res.success) {
                    setTournamentData(res.data.dataList || []);
                    setEliminationType(res.data.dataList[0].mtmkgTycd);
                }
            });
        } catch (error) {
            console.error('Error loading tournament data:', error);
        } finally {
            setLoading(false);
        }
    };

    // SEED_TYPE별로 데이터 분류
    const groupBySeedType = () => {
        const grouped = {
            winner: [],      // SEED_TYPE: 'W'
            loser: [],       // SEED_TYPE: 'L'
            loserFinal: [],  // SEED_TYPE: 'LF'
            grandFinal: []   // SEED_TYPE: 'GF'
        };

        tournamentData.forEach(seed => {
            if (seed.seedType === 'W') {
                grouped.winner.push(seed);
            } else if (seed.seedType === 'L') {
                grouped.loser.push(seed);
            } else if (seed.seedType === 'LF') {
                grouped.loserFinal.push(seed);
            } else if (seed.seedType === 'GF') {
                grouped.grandFinal.push(seed);
            }
        });

        return grouped;
    };

    // 승자조 라운드별 그룹화 (간단한 절반씩 줄어드는 구조)
    const groupWinnerByRound = (matches) => {
        if (!matches || matches.length === 0) return [];

        // seedNo를 기준으로 정렬
        const sortedMatches = [...matches].sort((a, b) => a.seedNo - b.seedNo);

        const rounds = [];
        let currentRoundSize = Math.ceil(sortedMatches.length / 2);
        let matchIndex = 0;

        // 첫 라운드는 전체 매치의 절반
        while (matchIndex < sortedMatches.length) {
            const round = [];
            const takeCount = Math.min(currentRoundSize, sortedMatches.length - matchIndex);

            for (let i = 0; i < takeCount; i++) {
                round.push(sortedMatches[matchIndex++]);
            }

            rounds.push(round);
            currentRoundSize = Math.ceil(currentRoundSize / 2);
        }

        return rounds;
    };

    // 패자조 라운드별 그룹화 (통합 버전 - 4강, 8강, 16강, 32강 모두 지원)
    // 승자조 각 라운드의 패자들을 패자조 같은 라운드로 배치
    const groupLoserByRound = (matches) => {
        if (!matches || matches.length === 0) return [];

        // seedNo를 기준으로 정렬
        const sortedMatches = [...matches].sort((a, b) => a.seedNo - b.seedNo);

        // 첫 번째 패자조 시드 번호로 토너먼트 크기 판정
        const firstLoserSeedNo = sortedMatches[0].seedNo;

        // 토너먼트 크기별 패자조 라운드 범위 정의
        const loserRoundRanges = {
            // 4강: 패자조 시드 4 (1개 라운드)
            4: [
                { start: 4, end: 4, round: 1 }
            ],
            // 8강: 패자조 시드 8-12 (3개 라운드)
            8: [
                { start: 8, end: 9, round: 1 },   // L-R1
                { start: 10, end: 11, round: 2 }, // L-R2
                { start: 12, end: 12, round: 3 }  // L-R3
            ],
            // 16강: 패자조 시드 16-28 (5개 라운드)
            16: [
                { start: 16, end: 19, round: 1 }, // L-R1
                { start: 20, end: 23, round: 2 }, // L-R2
                { start: 24, end: 25, round: 3 }, // L-R3
                { start: 26, end: 27, round: 4 }, // L-R4
                { start: 28, end: 28, round: 5 }  // L-R5
            ],
            // 32강: 패자조 시드 32-60 (7개 라운드)
            32: [
                { start: 32, end: 39, round: 1 }, // L-R1
                { start: 40, end: 47, round: 2 }, // L-R2
                { start: 48, end: 51, round: 3 }, // L-R3
                { start: 52, end: 55, round: 4 }, // L-R4
                { start: 56, end: 57, round: 5 }, // L-R5
                { start: 58, end: 59, round: 6 }, // L-R6
                { start: 60, end: 60, round: 7 }  // L-R7
            ]
        };

        // 토너먼트 크기 결정
        let tournamentSize = 4;
        if (firstLoserSeedNo >= 32) {
            tournamentSize = 32;
        } else if (firstLoserSeedNo >= 16) {
            tournamentSize = 16;
        } else if (firstLoserSeedNo >= 8) {
            tournamentSize = 8;
        }

        // 해당 토너먼트 크기의 라운드 범위 가져오기
        const roundRanges = loserRoundRanges[tournamentSize];

        // console.log('패자조 그룹화 디버그:', {
        //     tournamentSize,
        //     firstLoserSeedNo,
        //     totalMatches: sortedMatches.length,
        //     roundRanges,
        //     seedNumbers: sortedMatches.map(m => m.seedNo)
        // });

        // 각 패자조 시드에 대해 라운드 할당
        const loserWithRound = sortedMatches.map(loserMatch => {
            // seedNo 범위로 라운드 판정
            let assignedRound = 0;
            for (const range of roundRanges) {
                if (loserMatch.seedNo >= range.start && loserMatch.seedNo <= range.end) {
                    assignedRound = range.round;
                    break;
                }
            }

            return {
                ...loserMatch,
                sourceRound: assignedRound
            };
        });

        // sourceRound를 기준으로 라운드 그룹화
        const roundMap = new Map();
        loserWithRound.forEach(match => {
            if (!roundMap.has(match.sourceRound)) {
                roundMap.set(match.sourceRound, []);
            }
            roundMap.get(match.sourceRound).push(match);
        });

        // Map을 배열로 변환하고 sourceRound 순으로 정렬
        const rounds = Array.from(roundMap.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([_, matchesInRound]) => matchesInRound.sort((a, b) => a.seedNo - b.seedNo));

        // console.log('패자조 라운드 결과:', {
        //     totalRounds: rounds.length,
        //     roundDetails: rounds.map((round, idx) => ({
        //         roundIndex: idx + 1,
        //         matchCount: round.length,
        //         seedNumbers: round.map(m => m.seedNo)
        //     }))
        // });

        return rounds;
    };

    /**
     * 시드의 라운드 번호 계산 함수
     * @param {Object} seed - 시드 객체
     * @param {Array} allSeeds - 전체 시드 데이터
     * @returns {number} 라운드 번호
     */
    const calculateRound = (seed, allSeeds) => {
        if (seed.seedType === 'W') {
            // 승자조: seedNo 기반으로 라운드 계산
            const winnerSeeds = allSeeds.filter(s => s.seedType === 'W').sort((a, b) => a.seedNo - b.seedNo);
            const totalMatches = winnerSeeds.length;

            // 전체 토너먼트 크기 추정 (4, 8, 16, 32)
            let tournamentSize = 4;
            if (totalMatches >= 31) tournamentSize = 32;
            else if (totalMatches >= 15) tournamentSize = 16;
            else if (totalMatches >= 7) tournamentSize = 8;

            // 각 라운드의 시작 seedNo 계산
            const roundStarts = [];
            let start = 1;
            let size = tournamentSize / 2;
            while (size >= 1) {
                roundStarts.push(start);
                start += size;
                size = size / 2;
            }

            // seedNo가 어느 라운드에 속하는지 찾기
            for (let i = roundStarts.length - 1; i >= 0; i--) {
                if (seed.seedNo >= roundStarts[i]) {
                    return i + 1;
                }
            }
            return 1;
        } else if (seed.seedType === 'L') {
            // 패자조: seedNo 범위 기반으로 라운드 계산
            const loserSeeds = allSeeds.filter(s => s.seedType === 'L').sort((a, b) => a.seedNo - b.seedNo);
            if (loserSeeds.length === 0) return 0;

            const firstLoserSeedNo = loserSeeds[0].seedNo;

            const loserRoundRanges = {
                4: [
                    { start: 4, end: 4, round: 1 }
                ],
                8: [
                    { start: 8, end: 9, round: 1 },
                    { start: 10, end: 11, round: 2 },
                    { start: 12, end: 12, round: 3 }
                ],
                16: [
                    { start: 16, end: 19, round: 1 },
                    { start: 20, end: 23, round: 2 },
                    { start: 24, end: 25, round: 3 },
                    { start: 26, end: 27, round: 4 },
                    { start: 28, end: 28, round: 5 }
                ],
                32: [
                    { start: 32, end: 39, round: 1 },
                    { start: 40, end: 47, round: 2 },
                    { start: 48, end: 51, round: 3 },
                    { start: 52, end: 55, round: 4 },
                    { start: 56, end: 57, round: 5 },
                    { start: 58, end: 59, round: 6 },
                    { start: 60, end: 60, round: 7 }
                ]
            };

            let tournamentSize = 4;
            if (firstLoserSeedNo >= 32) tournamentSize = 32;
            else if (firstLoserSeedNo >= 16) tournamentSize = 16;
            else if (firstLoserSeedNo >= 8) tournamentSize = 8;

            const roundRanges = loserRoundRanges[tournamentSize];
            for (const range of roundRanges) {
                if (seed.seedNo >= range.start && seed.seedNo <= range.end) {
                    return range.round;
                }
            }
        }
        return 0;
    };

    /**
     * 승자 이동 위치 계산 함수
     * @param {Object} currentSeed - 현재 시드
     * @param {Array} allSeeds - 전체 시드 데이터
     * @returns {string} 'A' 또는 'B'
     */
    const calculateWinnerPosition = (currentSeed, allSeeds) => {
        // 패자결승(LF)의 승자는 최종결승(GF)의 B로 이동
        if (currentSeed.seedType === 'LF') {
            return 'B';
        }

        // 패자조인 경우
        if (currentSeed.seedType === 'L') {
            const currentRound = calculateRound(currentSeed, allSeeds);

            // 현재 라운드의 시드 개수
            const currentRoundSeeds = allSeeds.filter(s => {
                if (s.seedType !== 'L') return false;
                const round = calculateRound(s, allSeeds);
                return round === currentRound;
            }).length;

            // 다음 라운드의 시드 개수 (seedWinNo가 속한 라운드)
            const nextSeed = allSeeds.find(s => s.seedNo === currentSeed.seedWinNo);
            if (nextSeed) {
                const nextRound = calculateRound(nextSeed, allSeeds);
                const nextRoundSeeds = allSeeds.filter(s => {
                    if (s.seedType !== nextSeed.seedType) return false;
                    const round = calculateRound(s, allSeeds);
                    return round === nextRound;
                }).length;

                // 다음 라운드 시드 개수가 현재와 동일하면 A로 이동
                if (nextRoundSeeds === currentRoundSeeds) {
                    return 'A';
                }

                // 다음 라운드 시드 개수가 현재보다 작으면 계산 로직 적용
                if (nextRoundSeeds < currentRoundSeeds) {
                    return currentSeed.seedNo % 2 === 1 ? 'A' : 'B';
                }
            }
        }

        // 승자조 또는 기본 로직
        return currentSeed.seedNo % 2 === 1 ? 'A' : 'B';
    };

    /**
     * 패자 이동 위치 계산 함수
     * @param {Object} currentSeed - 현재 시드
     * @param {Array} allSeeds - 전체 시드 데이터
     * @returns {string} 'A' 또는 'B'
     */
    const calculateLoserPosition = (currentSeed, allSeeds) => {
        const currentRound = calculateRound(currentSeed, allSeeds);

        // 승자조 1라운드가 아닌 경우 B로 이동
        if (currentSeed.seedType === 'W' && currentRound > 1) {
            return 'B';
        }

        // 승자조 1라운드이거나 기본 로직
        return currentSeed.seedNo % 2 === 1 ? 'A' : 'B';
    };

    // 선수 클릭 시 승자 선택 및 즉시 저장 (자유롭게 변경 가능)
    const handlePlayerClick = (seed, playerType) => {
        if (params.mtmkgStat !== 'D') {
            alert("대진상태를 '진행'으로 변경해야 합니다.");
            return;
        }
        const winnerId = playerType === 'A' ? seed.plyrCdA : seed.plyrCdB;
        const loserId = playerType === 'A' ? seed.plyrCdB : seed.plyrCdA;

        // Bye인 경우 처리 안함
        if (!winnerId || winnerId === 'null') return;

        // 이미 같은 승자가 선택된 경우 무시
        if (seed.winPlyr === winnerId) return;

        // 승자/패자 진출 정보 계산
        const winnerNext = calculateNextSeed(seed, seed.seedType, true);
        const loserNext = calculateNextSeed(seed, seed.seedType, false);

        // 승자 진출 정보 객체
        const winnerInfo = winnerNext ? {
            plyrCd: winnerId,
            nextSeedNo: seed.seedWinNo,
            nextPlayerPosition: winnerNext.nextPlayerPosition
        } : null;

        // 패자 진출 정보 객체 (더블 엘리미네이션인 경우)
        const loserInfo = loserNext ? {
            plyrCd: loserId,
            nextSeedNo: seed.seedLoseNo,
            nextPlayerPosition: loserNext.nextPlayerPosition
        } : null;

        // 저장 파라미터 구성
        const saveParams = {
            mtmkgCd: seed.mtmkgCd,
            armType: seed.armType,
            seedNo: seed.seedNo,
            plyrCdA: seed.plyrCdA,
            plyrCdB: seed.plyrCdB,
            winPlyr: winnerId,
            losePlyr: loserId,
            // 승자 진출 정보 객체
            winnerInfo: winnerInfo,
            // 패자 진출 정보 객체 (더블 엘리미네이션인 경우)
            loserInfo: loserInfo
        };

        ApiSave('/sys/matchtrmntmng/trmntBracket/save/info', saveParams, {isMsgShow: false}).then(res => {
            if (res && res.success) {
                // 데이터 새로고침
                loadTournamentData(params.mtmkgCd, params.armType);
                if (props.onGridSearch) {
                    props.onGridSearch();
                }
            }
        });
    };

    // 다음 라운드에서 플레이어가 A 또는 B 위치에 들어갈지 결정
    // 규칙: 현재 seedNo가 홀수면 A, 짝수면 B
    // const determinePlayerPosition = (currentSeed) => {
    //     const nextSeedNo = currentSeed.seedWinNo;
    //     let position = currentSeed.seedNo % 2 === 1 ? 'A' : 'B';

    //     const nextSeedEle = document.querySelector(`[data-seed="${nextSeedNo}"]`);
    //     if (nextSeedEle) {
    //         const plyrNmArr = nextSeedEle.querySelectorAll('.player-name');
    //         if (plyrNmArr.length > 0) {
    //             if (plyrNmArr[0].innerHTML !== 'TBD') {
    //                 position = 'B';
    //             } else if (plyrNmArr[1].innerHTML !== 'TBD') {
    //                 position = 'A';
    //             }  
    //         }
    //     }

    //     return position;
    // };

    // 다음 라운드 시드 번호 계산
    const calculateNextSeed = (currentSeed, seedType, isWinner) => {
        // 싱글 엘리미네이션 또는 승자조
        if (seedType === 'W') {
            if (isWinner) {
                return {
                    nextSeedType: 'W',
                    nextPlayerPosition: calculateWinnerPosition(currentSeed, tournamentData)
                };
            } else {
                // 패자는 패자조로 (더블 엘리미네이션인 경우)
                if (params.mtmkgTycd === 'D') {
                    return {
                        nextSeedType: 'L',
                        nextPlayerPosition: calculateLoserPosition(currentSeed, tournamentData)
                    };
                }
                return null;
            }
        }
        // 패자조
        else if (seedType === 'L') {
            if (isWinner) {
                // 패자조 승자는 다음 패자조 라운드로
                return {
                    nextSeedType: 'L',
                    nextPlayerPosition: calculateWinnerPosition(currentSeed, tournamentData)
                };
            } else {
                // 패자조 패자는 탈락
                return null;
            }
        }
        // 패자결승 (LF)
        else if (seedType === 'LF') {
            if (isWinner) {
                // 패자결승 승자는 최종결승으로
                return {
                    nextSeedType: 'GF',
                    nextPlayerPosition: 'B'
                };
            } else {
                // 패자결승 패자는 탈락
                return null;
            }
        }
        // 최종결승 (GF)
        else if (seedType === 'GF') {
            return null;
        }

        return null;
    };

    /**********************
     * 최종 순위 계산
    ***********************/
    const calculateRankings = () => {
        const rankings = {
            first: null,
            second: null,
            third: [],
            fourth: [],
            fifth: [],
            sixth: []
        };

        if (eliminationType === 'D') {
            // 더블 엘리미네이션
            const grandFinal = tournamentData.find(seed => seed.seedType === 'GF');
            const loserFinal = tournamentData.find(seed => seed.seedType === 'LF');

            // 1등: 최종결승 승자
            if (grandFinal && grandFinal.winPlyr) {
                rankings.first = {
                    id: grandFinal.winPlyr,
                    name: grandFinal.winPlyrNm
                };
                // 2등: 최종결승 패자
                if (grandFinal.losePlyr) {
                    rankings.second = {
                        id: grandFinal.losePlyr,
                        name: grandFinal.losePlyrNm
                    };
                }
            }

            // 3등: 패자결승 패자
            if (loserFinal && loserFinal.losePlyr) {
                rankings.third.push({
                    id: loserFinal.losePlyr,
                    name: loserFinal.losePlyrNm
                });
            }

            // 4-6위: 패자조를 라운드별로 그룹핑하여 순위 결정
            const grouped = groupBySeedType();
            const loserRounds = groupLoserByRound(grouped.loser);

            // 라운드를 역순으로 순회 (가장 높은 라운드부터)
            const rankKeys = ['fourth', 'fifth', 'sixth'];
            let rankIndex = 0;

            for (let i = loserRounds.length - 1; i >= 0 && rankIndex < rankKeys.length; i--) {
                const roundMatches = loserRounds[i];

                roundMatches.forEach(seed => {
                    if (seed.losePlyr && seed.losePlyr !== 'null') {
                        const player = {
                            id: seed.losePlyr,
                            name: seed.losePlyrNm
                        };

                        // 이미 상위 순위에 포함된 선수는 제외
                        const isAlreadyRanked =
                            (rankings.first && rankings.first.id === player.id) ||
                            (rankings.second && rankings.second.id === player.id) ||
                            rankings.third.some(p => p.id === player.id);

                        if (!isAlreadyRanked && rankIndex < rankKeys.length) {
                            rankings[rankKeys[rankIndex]].push(player);
                        }
                    }
                });

                // 이번 라운드에서 패자가 있었다면 다음 순위로
                if (rankings[rankKeys[rankIndex]].length > 0) {
                    rankIndex++;
                }
            }
        } else if (eliminationType === 'S') {
            // 싱글 엘리미네이션
            const finalMatch = tournamentData.find(seed => seed.seedType === 'W' && seed.seedWinNo === null);

            if (finalMatch && finalMatch.winPlyr) {
                rankings.first = {
                    id: finalMatch.winPlyr,
                    name: finalMatch.winPlyrNm
                };
                if (finalMatch.losePlyr) {
                    rankings.second = {
                        id: finalMatch.losePlyr,
                        name: finalMatch.losePlyrNm
                    };
                }
            }

            // 준결승 패자들을 공동 3위로
            const grouped = groupBySeedType();
            const winnerRounds = groupWinnerByRound(grouped.winner);
            if (winnerRounds.length >= 2) {
                const semiFinals = winnerRounds[winnerRounds.length - 2];
                semiFinals.forEach(seed => {
                    if (seed.losePlyr && seed.losePlyr !== finalMatch?.losePlyr) {
                        rankings.third.push({
                            id: seed.losePlyr,
                            name: seed.losePlyrNm
                        });
                    }
                });
            }
        }

        return rankings;
    };

    // 토너먼트 종료 및 최종 순위 저장
    const handleFinishTournament = async () => {
        if (!window.confirm('토너먼트를 종료하고 최종 순위를 저장하시겠습니까?')) {
            return;
        }

        // calculateRankings 함수를 사용하여 순위 계산
        const rankingsData = calculateRankings();

        // 1위가 없으면 결승이 완료되지 않은 것
        if (!rankingsData.first) {
            alert('결승 승자가 결정되지 않았습니다.');
            return;
        }

        // 순위 데이터를 API 저장 형식으로 변환
        const rankings = [];

        // 1위
        if (rankingsData.first) {
            rankings.push({
                mtmkgCd: params.mtmkgCd,
                armType: params.armType,
                plyrCd: rankingsData.first.id,
                plyrNm: rankingsData.first.name,
                rnk: 1
            });
        }

        // 2위
        if (rankingsData.second) {
            rankings.push({
                mtmkgCd: params.mtmkgCd,
                armType: params.armType,
                plyrCd: rankingsData.second.id,
                plyrNm: rankingsData.second.name,
                rnk: 2
            });
        }

        // 3위
        rankingsData.third.forEach(player => {
            rankings.push({
                mtmkgCd: params.mtmkgCd,
                armType: params.armType,
                plyrCd: player.id,
                plyrNm: player.name,
                rnk: 3
            });
        });

        // 4위
        rankingsData.fourth.forEach(player => {
            rankings.push({
                mtmkgCd: params.mtmkgCd,
                armType: params.armType,
                plyrCd: player.id,
                plyrNm: player.name,
                rnk: 4
            });
        });

        // 5위
        rankingsData.fifth.forEach(player => {
            rankings.push({
                mtmkgCd: params.mtmkgCd,
                armType: params.armType,
                plyrCd: player.id,
                plyrNm: player.name,
                rnk: 5
            });
        });

        // 6위
        rankingsData.sixth.forEach(player => {
            rankings.push({
                mtmkgCd: params.mtmkgCd,
                armType: params.armType,
                plyrCd: player.id,
                plyrNm: player.name,
                rnk: 6
            });
        });

        // console.log('최종 순위 데이터:', rankings);

        // 순위 데이터 저장
        try {
            const res = await ApiSave('/sys/matchtrmntmng/trmntBracket/save/rankings', {
                trmntRankResultList: rankings,
                mtmkgCd: params.mtmkgCd,
                armType: params.armType
            }, {isMsgShow: false});

            if (res && res.success) {
                alert('토너먼트가 종료되고 최종 순위가 저장되었습니다.');
                handleToggle();
                if (props.onGridSearch) {
                    props.onGridSearch();
                }
            }
        } catch (error) {
            console.error('순위 저장 오류:', error);
            alert('순위 저장 중 오류가 발생했습니다.');
        }
    };

    

    // 승자 취소 (재설정)
    // const handleResetWinner = (seed) => {
    //     if (!seed.winPlyr) return;

    //     if (!window.confirm('승자를 재설정하시겠습니까?')) return;

    //     const saveParams = {
    //         mtmkgCd: seed.mtmkgCd,
    //         armType: seed.armType,
    //         seedNo: seed.seedNo,
    //         seedType: seed.seedType,
    //         plyrCdA: seed.plyrCdA,
    //         plyrCdB: seed.plyrCdB,
    //         winPlyr: null,
    //         losePlyr: null,
    //     };

    //     ApiSave('/sys/matchtrmntmng/save/info', saveParams, {isMsgShow: false}).then(res => {
    //         if (res && res.success) {
    //             loadTournamentData(params.mtmkgCd, params.armType);
    //             if (props.onGridSearch) {
    //                 props.onGridSearch();
    //             }
    //         }
    //     });
    // };

    // 매치 렌더링 (개선된 UI - 자유롭게 승자 변경 가능)
    const renderMatch = (seed, roundIndex) => {
        const isPlayerAWinner = seed.winPlyr === seed.plyrCdA;
        const isPlayerBWinner = seed.winPlyr === seed.plyrCdB;
        const hasWinner = seed.winPlyr !== null && seed.winPlyr !== undefined;
        // 선수가 한 명이라도 있으면 선택 가능 (TBD vs 실제 플레이어도 포함)
        const canSelect = seed.plyrCdA || seed.plyrCdB;
        const isClickable = canSelect;

        return (
            <div
                key={`${seed.seedType}-${seed.seedNo}`}
                className={`bracket-match ${hasWinner ? 'completed' : ''} ${isClickable ? 'selectable' : ''}`}
                data-seed={seed.seedNo}
                data-round={roundIndex}
            >
                <div className="match-number">
                    <span style={{ fontSize: '15px' }}>#{seed.seedNo}</span>
                    {!seed.plyrCdA && !seed.plyrCdB && (
                        <div className="match-waiting">
                            <small className="text-muted" style={{ fontSize: '9px' }}>대기 중</small>
                        </div>
                    )}
                    {/* {hasWinner && (
                        <button
                            className="btn btn-xs btn-outline-danger ms-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleResetWinner(seed);
                            }}
                            title="승자 재설정"
                        >
                            ↺
                        </button>
                    )} */}
                </div> 

                <div className="match-players-container">
                    <div
                        className={`match-player ${isPlayerAWinner ? 'winner' : ''} ${!seed.plyrCdA || seed.plyrCdA === 'null' ? 'bye' : ''} ${isClickable ? 'clickable' : ''}`}
                        onClick={() => isClickable && handlePlayerClick(seed, 'A')}
                        style={{ cursor: isClickable ? 'pointer' : 'default' }}
                    >
                        <span className="player-name">
                            {seed.plyrNmA || 'TBD'}
                        </span>
                        {isPlayerAWinner && <span className="winner-icon">👑</span>}
                    </div>

                    <div
                        className={`match-player ${isPlayerBWinner ? 'winner' : ''} ${!seed.plyrCdB || seed.plyrCdB === 'null' ? 'bye' : ''} ${isClickable ? 'clickable' : ''}`}
                        onClick={() => isClickable && handlePlayerClick(seed, 'B')}
                        style={{ cursor: isClickable ? 'pointer' : 'default' }}
                    >
                        <span className="player-name">
                            {seed.plyrNmB || 'TBD'}
                        </span>
                        {isPlayerBWinner && <span className="winner-icon">👑</span>}
                    </div>
                </div>
            </div>
        );
    };

    // 싱글 엘리미네이션 렌더링
    const renderSingleElimination = () => {
        const grouped = groupBySeedType();
        const winnerRounds = groupWinnerByRound(grouped.winner);

        // 결승전 승자 확인
        const finalRound = winnerRounds[winnerRounds.length - 1];
        const hasFinalWinner = finalRound && finalRound[0] && finalRound[0].winPlyr;

        return (
            <div className="tournament-bracket-wrapper">
                <div className="section-header bg-primary text-white p-2 rounded d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">🏆 싱글 엘리미네이션</h5>
                    {hasFinalWinner && params.mtmkgStat === 'D' && (
                        <button
                            className="btn btn-sm btn-light"
                            onClick={handleFinishTournament}
                        >
                            🏁 토너먼트 결과 등록
                        </button>
                    )}
                </div>

                <div className="bracket-rounds-container">
                    {winnerRounds.map((round, roundIndex) => (
                        <div key={`round-${roundIndex}`} className="bracket-round">
                            <div className="round-title">
                                <span className="badge bg-primary">
                                    {roundIndex === winnerRounds.length - 1 ? '결승' : `Round ${roundIndex + 1}`}
                                </span>
                            </div>
                            <div className="round-matches">
                                {round.map(seed => renderMatch(seed, roundIndex))}
                            </div>
                            {roundIndex < winnerRounds.length - 1 && (
                                <div className="round-connector">
                                    <svg className="connector-lines" width="100%" height="100%">
                                        {round.map((seed, idx) => {
                                            if (idx % 2 === 0 && idx + 1 < round.length) {
                                                return (
                                                    <g key={`connector-${idx}`}>
                                                        <line
                                                            x1="0"
                                                            y1={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                            x2="50%"
                                                            y2={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                            stroke="#0d6efd"
                                                            strokeWidth="2"
                                                        />
                                                        <line
                                                            x1="0"
                                                            y1={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                            x2="50%"
                                                            y2={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                            stroke="#0d6efd"
                                                            strokeWidth="2"
                                                        />
                                                        <line
                                                            x1="50%"
                                                            y1={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                            x2="50%"
                                                            y2={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                            stroke="#0d6efd"
                                                            strokeWidth="2"
                                                        />
                                                        <line
                                                            x1="50%"
                                                            y1={`${((idx * 100 / round.length) + ((idx + 1) * 100 / round.length)) / 2 + (50 / round.length)}%`}
                                                            x2="100%"
                                                            y2={`${((idx * 100 / round.length) + ((idx + 1) * 100 / round.length)) / 2 + (50 / round.length)}%`}
                                                            stroke="#0d6efd"
                                                            strokeWidth="2"
                                                        />
                                                    </g>
                                                );
                                            }
                                            return null;
                                        })}
                                    </svg>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // 더블 엘리미네이션 렌더링
    const renderDoubleElimination = () => {
        const grouped = groupBySeedType();
        const winnerRounds = groupWinnerByRound(grouped.winner);
        const loserRounds = groupLoserByRound(grouped.loser); // 통합 함수 사용 (4강, 8강, 16강, 32강 모두 지원)
        const loserFinals = grouped.loserFinal;
        const grandFinals = grouped.grandFinal;
        
        // console.log('winnerRounds', winnerRounds);
        // console.log('loserRounds', loserRounds);

        return (
            <div className="tournament-bracket-wrapper double-elimination">
                {/* 승자조 (Winner Bracket) */}
                <div className="bracket-section mb-4">
                    <div className="section-header bg-success text-white p-2 rounded">
                        <h5 className="mb-0">🏆 승자조 (Winner Bracket)</h5>
                    </div>
                    <div className="bracket-rounds-container mt-2">
                        {winnerRounds.map((round, roundIndex) => {
                            // 동적 라운드 이름 생성
                            let roundName = '';
                            const totalRounds = winnerRounds.length;

                            if (roundIndex === totalRounds - 1) {
                                roundName = 'W-결승';
                            } else if (roundIndex === totalRounds - 2) {
                                roundName = 'W-준결승';
                            } else if (roundIndex === totalRounds - 3) {
                                roundName = 'W-8강';
                            } else if (roundIndex === totalRounds - 4) {
                                roundName = 'W-16강';
                            } else if (roundIndex === totalRounds - 5) {
                                roundName = 'W-32강';
                            } else {
                                roundName = `W-R${roundIndex + 1}`;
                            }

                            return (
                            <div key={`winner-round-${roundIndex}`} className="bracket-round">
                                <div className="round-title">
                                    <span className="badge bg-success">
                                        {roundName}
                                    </span>
                                </div>
                                <div className="round-matches">
                                    {round.map(seed => renderMatch(seed, roundIndex))}
                                </div>
                                {roundIndex < winnerRounds.length - 1 && (
                                    <div className="round-connector">
                                        <svg className="connector-lines" width="100%" height="100%">
                                            {round.map((seed, idx) => {
                                                if (idx % 2 === 0 && idx + 1 < round.length) {
                                                    return (
                                                        <g key={`w-connector-${idx}`}>
                                                            <line x1="0" y1={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                                  x2="50%" y2={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                                  stroke="#198754" strokeWidth="2"/>
                                                            <line x1="0" y1={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                                  x2="50%" y2={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                                  stroke="#198754" strokeWidth="2"/>
                                                            <line x1="50%" y1={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                                  x2="50%" y2={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                                  stroke="#198754" strokeWidth="2"/>
                                                            <line x1="50%"
                                                                  y1={`${((idx * 100 / round.length) + ((idx + 1) * 100 / round.length)) / 2 + (50 / round.length)}%`}
                                                                  x2="100%"
                                                                  y2={`${((idx * 100 / round.length) + ((idx + 1) * 100 / round.length)) / 2 + (50 / round.length)}%`}
                                                                  stroke="#198754" strokeWidth="2"/>
                                                        </g>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </svg>
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                </div>

                {/* 패자조 (Loser Bracket) */}
                {loserRounds.length > 0 && (
                    <div className="bracket-section mb-4">
                        <div className="section-header bg-warning text-dark p-2 rounded">
                            <h5 className="mb-0">⚔️ 패자조 (Loser Bracket)</h5>
                        </div>
                        <div className="bracket-rounds-container mt-2">
                            {loserRounds.map((round, roundIndex) => {
                                return (
                                    <div key={`loser-round-${roundIndex}`} className="bracket-round">
                                        <div className="round-title">
                                            <span className="badge bg-warning text-dark">L-R{roundIndex + 1}</span>
                                        </div>
                                        <div className="round-matches">
                                            {round.map(seed => renderMatch(seed, roundIndex))}
                                        </div>
                                        {/* seedWinNo가 있는 경우에만 연결선 표시 (패자결승으로 가는 경우 제외 가능) */}
                                        {roundIndex < loserRounds.length - 1 && (
                                            <div className="round-connector">
                                                <svg className="connector-lines" width="100%" height="100%">
                                                    {round.map((currentSeed, currentIdx) => {
                                                        // seedWinNo가 없으면 연결선 없음
                                                        if (!currentSeed.seedWinNo) return null;

                                                        // 다음 라운드에서 seedWinNo와 일치하는 매치 찾기
                                                        let targetRoundIndex = -1;
                                                        let targetMatchIndex = -1;

                                                        // 모든 다음 라운드를 순회하면서 찾기
                                                        for (let r = roundIndex + 1; r < loserRounds.length; r++) {
                                                            const idx = loserRounds[r].findIndex(s => s.seedNo === currentSeed.seedWinNo);
                                                            if (idx >= 0) {
                                                                targetRoundIndex = r;
                                                                targetMatchIndex = idx;
                                                                break;
                                                            }
                                                        }

                                                        // 바로 다음 라운드에서만 연결 (한 칸만 이동)
                                                        if (targetRoundIndex === roundIndex + 1 && targetMatchIndex >= 0) {
                                                            const nextRound = loserRounds[targetRoundIndex];

                                                            // 현재 매치의 Y 위치 (%)
                                                            const currentY = (currentIdx / round.length) * 100 + (50 / round.length);
                                                            // 다음 매치의 Y 위치 (%)
                                                            const nextY = (targetMatchIndex / nextRound.length) * 100 + (50 / nextRound.length);

                                                            return (
                                                                <g key={`l-connector-${currentIdx}`}>
                                                                    {/* 현재 매치에서 오른쪽으로 */}
                                                                    <line
                                                                        x1="0"
                                                                        y1={`${currentY}%`}
                                                                        x2="50%"
                                                                        y2={`${currentY}%`}
                                                                        stroke="#ffc107"
                                                                        strokeWidth="2"
                                                                    />
                                                                    {/* 수직선 */}
                                                                    <line
                                                                        x1="50%"
                                                                        y1={`${currentY}%`}
                                                                        x2="50%"
                                                                        y2={`${nextY}%`}
                                                                        stroke="#ffc107"
                                                                        strokeWidth="2"
                                                                    />
                                                                    {/* 다음 매치로 */}
                                                                    <line
                                                                        x1="50%"
                                                                        y1={`${nextY}%`}
                                                                        x2="100%"
                                                                        y2={`${nextY}%`}
                                                                        stroke="#ffc107"
                                                                        strokeWidth="2"
                                                                    />
                                                                </g>
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* 패자결승 (Loser Final) */}
                {loserFinals.length > 0 && (
                    <div className="bracket-section mb-4">
                        <div className="section-header bg-info text-white p-2 rounded">
                            <h5 className="mb-0">🥈 패자결승 (Loser Final)</h5>
                        </div>
                        <div className="finals-container mt-2">
                            <div className="finals-matches">
                                {loserFinals.map((seed, idx) => renderMatch(seed, idx))}
                            </div>
                        </div>
                    </div>
                )}

                {/* 최종결승 (Grand Final) */}
                {grandFinals.length > 0 && (
                    <div className="bracket-section">
                        <div className="section-header bg-danger text-white p-2 rounded d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">🏅 최종결승 (Grand Final)</h5>
                            {grandFinals[0].winPlyr && params.mtmkgStat === 'D' && (
                                <button
                                    className="btn btn-sm btn-light"
                                    onClick={handleFinishTournament}
                                >
                                    🏁 토너먼트 결과 등록
                                </button>
                            )}
                        </div>
                        <div className="finals-container mt-2">
                            <div className="finals-matches">
                                {grandFinals.map((seed, idx) => renderMatch(seed, idx))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div
            className="modal modal-sheet p-1 modal-py-md-0 modal-overflow-h"
            style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
            tabIndex={-1}
            role="dialog"
            id={rootId}
        >
            <div className="modal-dialog" style={{ maxWidth: '98vw', width: '99vw', marginTop: 0 }}>
                <div className="modal-content rounded-4 shadow" style={{ height: '99vh', display: 'flex', flexDirection: 'column' }}>
                    <div className="modal-header border-bottom-0">
                        <div className="header-info ms-3">
                            <button
                                className="badge me-2"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleInitialize();
                                }}
                                style={{ color: "red" }}
                                title="토너먼트 초기화"
                            >
                                🔄 초기화
                            </button>
                            <span className="badge bg-info me-2">대진코드: {params.mtmkgCd}</span>
                            <span className="badge bg-secondary me-2">
                                {params.armType === 'R' ? '오른팔' : '왼팔'}
                            </span>
                            <span className="badge bg-primary me-2">
                                {params.mtmkgTycd === 'S' ? '싱글 엘리미네이션' : '더블 엘리미네이션'}
                            </span>
                            <span className="badge bg-primary me-2">
                                {params.mtmkgStat === 'R' ? '대기' : 
                                    params.mtmkgStat === 'C' ? '확정' : 
                                    params.mtmkgStat === 'D' ? '진행중' : '종료'
                                }
                            </span>
                        </div>
                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={handleToggle}
                        />
                    </div>

                    <div className="modal-body py-0" style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">로딩 중...</span>
                                </div>
                            </div>
                        ) : tournamentData.length === 0 ? (
                            <div className="alert alert-warning text-center my-3">
                                <p className="mb-1">토너먼트 데이터가 없습니다.</p>
                                <p className="mb-0">먼저 토너먼트를 생성해주세요.</p>
                            </div>
                        ) : (
                            params.mtmkgTycd === 'S' ? renderSingleElimination() : renderDoubleElimination()
                        )}
                    </div>

                    <div className="modal-footer border-top">
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <div className="text-muted small">
                                💡 승자는 언제든 변경 가능.
                            </div>
                            <button type="button" className="btn btn-secondary" onClick={handleToggle}>
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default CompFormtrmntBracket;
